import { useEffect, useRef } from 'react'
import Plyr from 'plyr'
import 'plyr/dist/plyr.css'

interface PlyrPlayerProps {
  src: string
  poster?: string
  autoPlay?: boolean
  muted?: boolean
  loop?: boolean
  controls?: boolean
  className?: string
  onEnded?: () => void
  onPlay?: () => void
  onPause?: () => void
}

export function PlyrPlayer({
  src,
  poster,
  autoPlay = false,
  muted = false,
  loop = false,
  controls = true,
  className = '',
  onEnded,
  onPlay,
  onPause,
}: PlyrPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<Plyr | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    // Create video element
    const video = document.createElement('video')
    video.src = src
    if (poster) video.poster = poster
    video.playsInline = true
    if (!src.startsWith('local-media://')) video.crossOrigin = 'anonymous'
    if (muted) video.muted = true
    if (loop) video.loop = true
    // Clear container and add video
    containerRef.current.innerHTML = ''
    containerRef.current.appendChild(video)

    // Initialize Plyr
    playerRef.current = new Plyr(video, {
      controls: controls
        ? [
            'play-large',
            'play',
            'progress',
            'current-time',
            'mute',
            'volume',
            'fullscreen',
          ]
        : [],
      hideControls: true,
      resetOnEnd: !loop,
      loop: { active: loop },
      muted,
      clickToPlay: controls,
    })

    // Event listeners
    playerRef.current.on('ready', () => {
      if (autoPlay) {
        video.play().catch(() => {
          // Autoplay was prevented, that's fine
        })
      }
    })

    if (onEnded) {
      playerRef.current.on('ended', onEnded)
    }
    if (onPlay) {
      playerRef.current.on('play', onPlay)
    }
    if (onPause) {
      playerRef.current.on('pause', onPause)
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy()
        playerRef.current = null
      }
    }
  }, [src, poster, autoPlay, muted, loop, controls, onEnded, onPlay, onPause])

  return (
    <div className={`plyr-wrapper ${className}`}>
      <div ref={containerRef} className="plyr-container" />
      <style>{`
        .plyr-wrapper {
          --plyr-color-main: rgba(255,255,255,0.85);
          --plyr-video-control-color: rgba(255,255,255,0.8);
          --plyr-video-control-background-hover: rgba(255,255,255,0.15);
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .plyr-wrapper .plyr-container {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }
        .plyr-wrapper .plyr {
          border-radius: 0.75rem;
          overflow: hidden;
          width: 100%;
          height: 100%;
        }
        .plyr-wrapper .plyr__control--overlaid {
          background: rgba(255,255,255,0.15) !important;
          backdrop-filter: blur(8px);
        }
        .plyr-wrapper .plyr__control--overlaid:hover {
          background: rgba(255,255,255,0.25) !important;
        }
        .plyr-wrapper .plyr--full-ui input[type=range] {
          color: rgba(255,255,255,0.85) !important;
        }
        .plyr-wrapper .plyr__video-wrapper {
          background: #000;
          height: 100% !important;
          padding-bottom: 0 !important;
        }
        .plyr-wrapper video {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      `}</style>
    </div>
  )
}
